import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import { ShieldCheck, Building2 } from "lucide-react";

const DEMO = [
  { email: "hse@permitco.fr", role: "Responsable HSE" },
  { email: "do@permitco.fr", role: "Donneur d'ordre" },
  { email: "admin@permitco.fr", role: "Administrateur" },
];

export default function Login() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login"); // login | register
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [organisation, setOrganisation] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const switchMode = (m) => { setMode(m); setErr(""); };

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else await register({ name, organisation, email, password });
    } catch (ex) { setErr(apiErr(ex, mode === "login" ? "Connexion impossible" : "Inscription impossible")); }
    finally { setBusy(false); }
  };

  const isReg = mode === "register";

  return (
    <div className="login-wrap">
      <div className="login-brand">
        <div>
          <div className="logo" style={{ padding: 0 }}>PERMIT<em style={{ color: "var(--ambre)", fontStyle: "normal" }}>&amp;</em>CO</div>
          <div className="logo-sub" style={{ padding: "6px 0 0" }}>Pilotage Plans de Prévention · Coactivité · Permis</div>
        </div>
        <div style={{ maxWidth: 420 }}>
          <ShieldCheck size={40} color="var(--ambre)" />
          <h2 style={{ fontSize: "1.6rem", margin: "16px 0 10px", color: "#fff" }}>Maîtrisez les interventions d'entreprises extérieures</h2>
          <p style={{ fontSize: ".9rem", color: "#B9CDCF", lineHeight: 1.5 }}>
            Opérations multi-sites, inspection commune, plan de prévention généré, moteur de coactivité et permis de travail — une décision opérationnelle par écran.
          </p>
        </div>
        <div style={{ fontFamily: "'Space Mono'", fontSize: ".6rem", color: "#7FA0A5", textTransform: "uppercase", letterSpacing: ".08em" }}>
          La décision finale d'autorisation reste humaine.
        </div>
      </div>

      <div className="login-form">
        <div className="login-card">
          <div className="auth-tabs" data-testid="auth-tabs">
            <button type="button" className={!isReg ? "on" : ""} onClick={() => switchMode("login")} data-testid="tab-login">Connexion</button>
            <button type="button" className={isReg ? "on" : ""} onClick={() => switchMode("register")} data-testid="tab-register">Créer un compte</button>
          </div>
          <h1 style={{ fontSize: "1.6rem", marginBottom: 4 }}>{isReg ? "Créez votre organisation" : "Connexion"}</h1>
          <p style={{ color: "var(--text-muted)", fontSize: ".85rem", marginBottom: 18 }}>
            {isReg ? "Un espace de pilotage isolé, rien qu'à vous." : "Accédez à votre espace de pilotage."}
          </p>
          <form onSubmit={submit}>
            {isReg && <>
              <label className="f">Nom complet</label>
              <input data-testid="register-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Prénom Nom" required />
              <label className="f">Nom de l'organisation</label>
              <input data-testid="register-organisation" value={organisation} onChange={(e) => setOrganisation(e.target.value)} placeholder="Ex : Groupe Immobilier Atlantique" required />
            </>}
            <label className="f">Email</label>
            <input data-testid={isReg ? "register-email" : "login-email"} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            <label className="f">Mot de passe</label>
            <input data-testid={isReg ? "register-password" : "login-password"} type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={isReg ? "6 caractères minimum" : ""} minLength={isReg ? 6 : undefined} required />
            {err && <div className="notice" style={{ background: "var(--rouge-p)", borderColor: "var(--rouge)", marginTop: 12 }} data-testid="auth-error">{err}</div>}
            <button className="btn btn-p" data-testid={isReg ? "register-submit" : "login-submit"} disabled={busy} style={{ width: "100%", marginTop: 16, justifyContent: "center" }}>
              {busy ? (isReg ? "Création…" : "Connexion…") : (isReg ? <><Building2 size={15} />Créer mon organisation</> : "Se connecter")}
            </button>
          </form>

          {isReg ? (
            <p style={{ marginTop: 16, fontSize: ".82rem", color: "var(--text-muted)" }}>
              Déjà un compte ? <a href="#login" onClick={(e) => { e.preventDefault(); switchMode("login"); }} data-testid="goto-login">Se connecter</a>
            </p>
          ) : (
            <>
              <p style={{ marginTop: 16, fontSize: ".82rem", color: "var(--text-muted)" }}>
                Nouveau ? <a href="#register" onClick={(e) => { e.preventDefault(); switchMode("register"); }} data-testid="goto-register">Créez votre organisation</a>
              </p>
              <div className="demo-accounts">
                <div className="f">Comptes de démonstration (cliquez pour préremplir l'e-mail)</div>
                {DEMO.map((d) => (
                  <div key={d.email} className="row" onClick={() => setEmail(d.email)} data-testid={`demo-${d.email}`}>
                    <span className="mono">{d.email}</span><span style={{ color: "var(--text-muted)" }}>{d.role}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
